import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRow, GridRowId, GridRowProps } from '@mui/x-data-grid';
import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CareTeamWrapper, WrappedCareTeam } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CareTeam';
import {
  OrganizationWrapper,
  WrappedOrganization,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { IdentifierCode, WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import {
  PractitionerWrapper,
  WrappedPractitioner,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Practitioner';
import {
  PractitionerRoleWrapper,
  WrappedPractitionerRole,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PractitionerRole';
import {
  RelatedPersonWrapper,
  WrappedRelatedPerson,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/RelatedPerson';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import {
  useOrganizations,
  usePractitioners,
  useRelatedPersons,
  useValueSets,
} from 'src/careflow/ph-core/fhir-client/fhir-react';
import {
  CareTeamMember,
  CareTeamMemberWithEditableData,
  CareTeamPermissions,
  MemberEditableData,
  Option,
} from 'src/@types/crs/case';
import { fhirClient } from 'src/App';
import { GridItem, GridSection } from 'src/components/CustomModal';
import DatePickerMoment from 'src/components/DatePickerMoment';
import SearchTextField from 'src/components/SearchTextField';
import useObjectState from 'src/hooks/useObjectState';
import usePermissionsContext from 'src/careflow/ph-core/admin/permissions/hook/usePermissionsContext';
import {
  Bundle,
  CareTeam,
  Coding,
  FhirResource,
  Organization,
  Practitioner,
  PractitionerRole,
} from 'src/careflow/ph-core/fhir-client/resources';
import CellRow from 'src/careflow/common/CellRow';
import { NOTAPPLICABLE, iconGreyFillColor, isActiveCondition } from 'src/careflow/helpers/constants';
import { 
  fetchOrganizationAffiliation, 
  getReference, 
  searchIfContainedInObj 
} from 'src/careflow/helpers/common';
import { ResourceWithIncludedResources } from 'src/careflow/helpers/types';
import { checkIfDateIsAfterAnother } from 'src/utils/dates';
import { 
  getFhirIdFromEntity, 
  getIncludedResources, 
  getReferenceId, 
  getReferenceType, 
  getResourcesAndRelatedResources 
} from 'src/utils/fhir';
import { cleanSearchInput } from 'src/utils/string';
import { debounce } from 'src/utils/timers';
import { capitalize, isArray, isEmpty, isString } from 'lodash';
import { getChildOrganizations } from './api';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import Iconify from 'src/components/Iconify';
import { 
  Cake, 
  Mail, 
  Phone, 
  Wc, 
  ExpandLess as ExpandLessIcon, 
  ExpandMore as ExpandMoreIcon, 
  Business, 
  Work 
} from '@mui/icons-material';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import uuidv4 from 'src/utils/uuidv4';
import { CollapsibleRow } from '../../../../../ph-core/grid/react/CollapsibleDataGrid';
import { wrapResource } from 'src/utils/fhirSystem';
import { internalExternalOrg } from 'src/careflow/care-coordination/inbound-referral/constants';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';

const iconSize: CSSProperties = { height: 15, width: 15 };

export interface SearchMemberState {
  searchTextField: string;
  filter: string | null;
  resourceType: string | null;
  resourceName?: string | null;
}

export type MemberResourceTypes =
  | 'Patient'
  | 'Practitioner'
  | 'PractitionerRole'
  | 'CareTeam'
  | 'Organization'
  | 'RelatedPerson'
  | 'Me';

export type onSelectResource = (
  resource: MemberWrappedResourceTypes,
  includedResources?: FhirResource[],
  row?: GridRow | null
) => void;

export type MemberWrappedResourceTypes =
  | WrappedPatient
  | WrappedCareTeam
  | WrappedPractitioner
  | WrappedPractitionerRole
  | WrappedOrganization
  | WrappedRelatedPerson;

export interface GridRow extends Partial<CareTeamMember> {
  id: string;
  memberType: string;
  isSelected?: boolean;
  resource?: MemberWrappedResourceTypes;
  includedResources?: FhirResource[];
}

export interface SearchMemberProps {
  patient?: WrappedPatient;
  showMemberTypeField?: boolean;
  filterRole?: string | null;
  onSelectResource?: onSelectResource;
  externalResourceType?: MemberResourceTypes;
  onClear?: () => void;
  isEditable?: boolean;
  careTeamMembers?: CareTeamMember[];
  isLoading?: boolean;
  membersSelectionRef?: React.MutableRefObject<CareTeamMemberWithEditableData[]>;
  headerComponents?: React.ReactNode;
  includeGeneralPractitioner?: boolean;
  onlyCareTeam?: boolean;
  memberTypesByConfig?: any;
  filterOrganizationCode?: string;
  handlerMemberPractitionerRole?: any;
  externalRefresh?: boolean;
  onAddNewPractitioner?: () => void;
}

export type MemberResourceTypeOption = Option<MemberResourceTypes>;

export const pagination = {
  pageSize: 50,
};

export const columnsDefaultWidth = 250;

export const getDefaultColumnWidth = (resourceType: string | null, columnWidth = columnsDefaultWidth) => {
  if (!resourceType) return { width: columnWidth, flex: 0 };
  return {};
};

const SearchMember = ({
  externalResourceType,
  filterRole,
  onSelectResource,
  onClear,
  patient,
  showMemberTypeField = false,
  isEditable = false,
  careTeamMembers,
  isLoading,
  membersSelectionRef,
  headerComponents,
  includeGeneralPractitioner,
  onlyCareTeam,
  memberTypesByConfig,
  filterOrganizationCode,
  handlerMemberPractitionerRole,
  externalRefresh,
  onAddNewPractitioner,
}: SearchMemberProps) => {
  const user = useAuth();
  const queryClient = useQueryClient();

  const { componentsData, configurations } = useTenantConfigData();
  const { selectCareTeam, selectMember } = componentsData?.searchMember ?? {};
  
  const { i18n } = useLocales();

  const [allowedOrganizations, setAllowedOrganizations] = useState<any[]>([]);
  const [includeMembersInactive, setIncludeMembersInactive] = useState<any>([]);
  const [internalExternal, setInternalExternal] = useState<string[]>(['all']);
  const [organizationInternalExternal, setOrganizationInternalExternal] = useState<string[]>([]);

  const userOrgFhirId = useMemo(() => {
    if (user) {
      const selectedOrg = user.getSelectedOrg();
      if (
        selectedOrg &&
        selectedOrg?.fhir_uri &&
        isString(selectedOrg?.fhir_uri) &&
        selectedOrg?.fhir_uri.includes('Organization/')
      )
        try {
          return selectedOrg?.fhir_uri.split('Organization/')?.[1];
        } catch (error) {
          return null;
        }
    }
    return null;
  }, [user]);

  const getAllowedOrganizations = async (fhirId: string) => {
    if (fhirId) {
      try {
        const result = await getChildOrganizations([fhirId]);
        if (result && isArray(result)) {
          setAllowedOrganizations(result);
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    if (userOrgFhirId) {
      getAllowedOrganizations(userOrgFhirId);
    }
  }, [userOrgFhirId]);

  useEffect(() => {
    const getOrgsInternalExternal = async () => {
      if (!internalExternal?.length || internalExternal.includes('all')) {
        setOrganizationInternalExternal([]);
        return;
      };

      const userOrgId = userOrgFhirId ? getFhirIdFromEntity(userOrgFhirId) : "";
      const checkedInternal = internalExternal.includes('internal');
      const checkedExternal = internalExternal.includes('external');
  
      let result: string[] = [];
  
      if (checkedExternal && userOrgId) {
        const affiliations = await fetchOrganizationAffiliation(allowedOrganizations);
        result = affiliations;
      }
  
      if (checkedInternal) {
        result = [...result, ...allowedOrganizations];
      }

      setOrganizationInternalExternal(Array.from(new Set(result)));
    };
  
    getOrgsInternalExternal();
  }, [internalExternal,allowedOrganizations]);

  const [{ filter, searchTextField, resourceType, resourceName }, updateState] =
    useObjectState<SearchMemberState>({
      searchTextField: '',
      filter: null,
      resourceType: null,
      resourceName: null
    });

  const [{ practitionerRoles, isPractitionerRolesLoading }, updatePractitionerRoleState] =
    useObjectState<{
      isPractitionerRolesLoading: boolean;
      practitionerRoles: ResourceWithIncludedResources[];
    }>({
      practitionerRoles: [],
      isPractitionerRolesLoading: false,
    });
  
  const [{ careTeams, isCareTeamsLoading }, updateCareTeamState] =
    useObjectState<{
      careTeams: {
        resource: FhirResource,
        includedResources: (FhirResource | ResourceWithIncludedResources<FhirResource>)[]
      }[];
      isCareTeamsLoading: boolean;
    }>({
      careTeams: [],
      isCareTeamsLoading: false,
    });

  const practitionerFilter = useMemo(() => {
    const cleansedSearch = filter ? cleanSearchInput(filter) : '';
    if (!cleansedSearch.length) return isActiveCondition;
    const splittedSearch = cleansedSearch ? cleansedSearch.split(' ') : [];
    const givenName = splittedSearch?.[0] ?? '';
    const familyName = splittedSearch.slice(1).join(' ');

    return splittedSearch.length === 1
      ? {
          name: givenName,
          ...isActiveCondition,
        }
      : {
          given: givenName,
          ...(familyName ? { family: familyName } : {}),
          ...isActiveCondition,
        };
  }, [filter]);

  const patientGeneralPracPractitionerRoles: string[] = useMemo(() => {
    if (!patient?.generalPractitioner) {
      return [];
    }

    let resoucesIds: string[] = [];

    patient.generalPractitioner.forEach((item) => {
      if (item.reference) {
        const splitRef = item.reference.split('/');
        const id = splitRef[splitRef.length - 1];
        resoucesIds.push(id);
      }
    });

    return resoucesIds;
  }, [patient]);

  const getPractitionerRoles = useCallback(async () => {
    // Only execute if we're showing PractitionerRole/Me OR if externalRefresh is true
    if (resourceType !== 'PractitionerRole' && resourceType !== 'Me' && !externalRefresh) return;
    try {
      updatePractitionerRoleState({ isPractitionerRolesLoading: true });
      const cleansedSearch = filter ? cleanSearchInput(filter) : '';
      const splittedSearch = cleansedSearch ? cleansedSearch.split(' ') : [];
      const givenName = splittedSearch?.[0] ?? '';
      const familyName = splittedSearch.slice(1).join(' ');

      const userPractitionerId = getReferenceId(user?.getCurrentUser()?.user_fhir_uri);

      const includeQuery = `_include=PractitionerRole:practitioner&_include=PractitionerRole:organization`;

      const query = resourceType === 'Me'
        ? `${includeQuery}&practitioner=${userPractitionerId}&organization=${userOrgFhirId}`
        : filterRole ? `${includeQuery}&role=${filterRole}` 
        : includeQuery;

      const filterPRByPractitioners = {
        ...(givenName && familyName
          ? {
              'practitioner.given': givenName,
              ...(familyName
                ? {
                    'practitioner.family': familyName,
                  }
                : {}),
            }
          : {
              'practitioner.name': cleansedSearch,
            }),
        ...isActiveCondition,
      };

      // Build organization filter - but don't exclude roles without organization
      const filterPRByOrganizations = {
        ...(cleansedSearch && { 'organization.name': cleansedSearch }),
        ...(organizationInternalExternal?.length > 0 && {organization: organizationInternalExternal?.join(',')}),
        ...isActiveCondition,
      };
      const filterPR = {
        ...(cleansedSearch ? { 'role:text': cleansedSearch } : {}),
        ...isActiveCondition,
      };

      const practitionerRolesResourcesUrl = `PractitionerRole?${new URLSearchParams(
        filterPR
      ).toString()}&${new URLSearchParams(filterPRByOrganizations).toString()}&${query}`;
      
      const practitionerRolesResources = await fhirClient.search<Bundle>(
        practitionerRolesResourcesUrl
      );

      const queryPRByPractitionersOrg = (!isEmpty(givenName) || !isEmpty(familyName)) && organizationInternalExternal?.length > 0 
          ? `${new URLSearchParams({organization: organizationInternalExternal?.join(',')}).toString()}` 
          : {};

      const filrterPRByPractitionersOrg = `${new URLSearchParams(
        filterPRByPractitioners
      ).toString()}&${queryPRByPractitionersOrg}&${query}`;

      const pRolesByPractitioner = cleansedSearch
        ? await fhirClient.search<Bundle>(
            `PractitionerRole?${filrterPRByPractitionersOrg}`
          )
        : null;

        const pRolesByOrganization = await fhirClient.search<Bundle>(
          `PractitionerRole?${new URLSearchParams(
            filterPRByOrganizations
          ).toString()}&${query}`
        );

      const patientPractitionerRolesResources =
        patientGeneralPracPractitionerRoles &&
        patientGeneralPracPractitionerRoles.length > 0 &&
        cleansedSearch === '' &&
        includeGeneralPractitioner
          ? await fhirClient.search<Bundle>(
              `PractitionerRole?_id=${patientGeneralPracPractitionerRoles.join(',')}`
            )
          : null;

      const [practitionerRoles, relatedResources] = getResourcesAndRelatedResources(
        [
          ...(patientPractitionerRolesResources?.entry ?? []),
          ...(practitionerRolesResources?.entry ?? []),
          ...(pRolesByPractitioner?.entry ?? []),
          ...(pRolesByOrganization?.entry ?? []),
        ].reduce<FhirResource[]>((resources, { resource }) => {
          if (resource) return [...resources, resource];
          return resources;
        }, []),
        'PractitionerRole'
      );
      updatePractitionerRoleState({
        practitionerRoles: (practitionerRoles as PractitionerRole[]).map((practitionerRole) => {
          // Find organization only if reference exists
          const organization = practitionerRole?.organization?.reference
            ? relatedResources.find(
                ({ id }) => id === practitionerRole?.organization?.reference?.split?.('/')?.[1]
              )
            : undefined;
          
          // Find practitioner only if reference exists
          const practitioner = practitionerRole?.practitioner?.reference
            ? relatedResources.find(
                ({ id }) => id === practitionerRole?.practitioner?.reference?.split?.('/')?.[1]
              )
            : undefined;
          
          const wrappedPractitioner = practitioner ? [PractitionerWrapper(practitioner as Practitioner)] : [];
          const practitionerActive = wrappedPractitioner?.filter(({ active }) => active);
          
          return {
            resource: PractitionerRoleWrapper(practitionerRole),
            includedResources: [
              ...(organization ? [OrganizationWrapper(organization as Organization)] : []),
              ...(practitionerActive ?? []),
            ],
          };
        }),
      });
    } catch (error) {}

    updatePractitionerRoleState({ isPractitionerRolesLoading: false });
  }, [filter, resourceType, allowedOrganizations, filterRole, organizationInternalExternal, externalRefresh]);

  useEffect(() => {
    if (allowedOrganizations && allowedOrganizations.length > 0) {
      getPractitionerRoles();
    }
  }, [getPractitionerRoles, allowedOrganizations]);

  // Additional effect to handle externalRefresh for PractitionerRole data
  useEffect(() => {
    if (externalRefresh && allowedOrganizations && allowedOrganizations.length > 0) {
      getPractitionerRoles();
    }
  }, [externalRefresh, allowedOrganizations, getPractitionerRoles]);

  const [membersSelected, setMembersSelected] = useState<CareTeamMember[]>([]);

  const [membersEditableData, setMembersEditableData] = useState<MemberEditableData[]>([]);

  const { isAllowedToEdit, isAllowedToDelete, isAllowedToAdd } =
    usePermissionsContext<CareTeamPermissions['members']>() ?? {};

  useEffect(() => {
    if (!careTeamMembers?.length) return;
    const membersEditableData: MemberEditableData[] = careTeamMembers.map(
      ({ id, memberType, endDateObj, startDateObj, role, roleId }) => ({
        id,
        memberType,
        startDate: startDateObj,
        endDate: endDateObj,
        role: roleId ? { code: roleId, display: role } : null,
        error: {
          startDate: null,
        },
      })
    );
    setMembersEditableData(membersEditableData);
    setMembersSelected(careTeamMembers);
  }, [careTeamMembers, filterRole]);

  useEffect(() => {
    if (!externalResourceType) return;
    console.log('SearchMember - Setting resourceType to:', externalResourceType);
    updateState({ resourceType: externalResourceType });
  }, [externalResourceType]);

  const findMemberEditableData = useCallback(
    (id: string, memberType: string): MemberEditableData | undefined =>
      membersEditableData.find(
        (memberEditableData) =>
          memberEditableData.id === id && memberEditableData.memberType === memberType
      ),
    [membersEditableData]
  );

  useEffect(() => {
    if (!membersSelectionRef?.current) return;
    const membersWithEditableData: CareTeamMemberWithEditableData[] = membersSelected.map(
      (memberSelected) => {
        const memberEditableData = (findMemberEditableData(
          memberSelected.id,
          memberSelected.memberType
        ) ?? {}) as MemberEditableData;
        return {
          ...memberSelected,
          editableData: {
            ...memberEditableData,
          },
        };
      }
    );
    membersSelectionRef.current = membersWithEditableData;
  }, [membersSelected, findMemberEditableData]);

  const [rolesRecords] = useValueSets({
    filter: {
      identifier: 'ph-roles-codes',
    },
    map: ValueSetWrapper,
  });

  const roles = useMemo(() => rolesRecords?.[0]?.asListAll() ?? null, [rolesRecords]);

  const getCareTeamWithMembers = useCallback(async () => {
    if (resourceType !== 'CareTeam') return [];
    try {
      updateCareTeamState({ isCareTeamsLoading: true });
      const url =  `CareTeam?status=active&_include=CareTeam:participant`;
      const response = await fhirClient.search<Bundle>(url);
      if (!response?.entry?.length) return [];
      let entries = response.entry.reduce<FhirResource[]>((resources, { resource }) => {
        if (!resource) return resources;
        return [...resources, resource];
      }, []);
      const careTeams = entries.filter(
        ({ resourceType }) => resourceType === 'CareTeam'
      ) as CareTeam[];

      if(isEditable) {
        const mapCareTeams = careTeams.reduce<{
          resource: FhirResource,
          includedResources: (FhirResource | ResourceWithIncludedResources<FhirResource>)[]
        }[]>(
          (resources, careTeamUnwrapped) => {
            if (!careTeamUnwrapped?.id) return resources;
            const careTeam = CareTeamWrapper(careTeamUnwrapped as CareTeam); 
            return [
              ...resources,
              {
                resource: careTeam,
                includedResources: [],
              },
            ];
            },
          []);
        updateCareTeamState({ careTeams: mapCareTeams });
        return;
      };

      const otherCareTeams = careTeams?.filter(
        (careTeam) =>
          !careTeam.subject &&
          careTeam.managingOrganization?.some(
            ({ reference }) => getReferenceId(reference) === userOrgFhirId
          )
      );

      const patientCareTeams = careTeams?.filter(
        (careTeam) =>
          getReferenceId(careTeam.subject?.reference) === patient?.id
      );

      const uniqueCareTeams = [
        ...new Map(
          [...otherCareTeams, ...patientCareTeams].map((careTeam) => [careTeam.id, careTeam])
        ).values(),
      ];

      const practitionerRoleRelatedResources = await getPractitionerRoleRelatedResources(uniqueCareTeams);
      const relatedPersonRelatedResources = await getRelatedPersonRelatedResources(uniqueCareTeams);

      entries = [...entries, ...practitionerRoleRelatedResources, ...relatedPersonRelatedResources];

      const mappedCareTeams = uniqueCareTeams
      .reduce<{
        resource: FhirResource,
        includedResources: (FhirResource | ResourceWithIncludedResources<FhirResource>)[]
      }[]>(
        (resources, careTeamUnwrapped) => {
          if (!careTeamUnwrapped?.id) return resources;
          const careTeam = CareTeamWrapper(careTeamUnwrapped as CareTeam);

          const participant =
            careTeam?.participant?.reduce?.<FhirResource[]>((resources, { member }) => {
              const resource = entries.find?.(({ id }) => id === getReferenceId(member?.reference));
              if (!resource) return resources;
              return [...resources, resource];
            }, []) ?? [];

          const prRelatedResources = participant.reduce<ResourceWithIncludedResources[]>((resources, resource) => {
            if (resource?.resourceType !== 'PractitionerRole') return resources;
            const practitionerRole = resource as PractitionerRole;

            const practitioner = entries.find(
              ({ id }) => id === getReferenceId(practitionerRole?.practitioner?.reference)
            );
            const practitionerRoleWithPractitioner = { 
              resource: PractitionerRoleWrapper(practitionerRole), 
              includedResources: practitioner && practitioner.resourceType === 'Practitioner' ? [PractitionerWrapper(practitioner)] : [] 
            };
            
            return [
              ...resources,
              practitionerRoleWithPractitioner,
            ];
          }, []);

          const relatedPersonResources = participant.reduce<any[]>((resources, resource) => {
            if (resource?.resourceType !== 'RelatedPerson') return resources;
            const relatedPerson = RelatedPersonWrapper(resource);
            
            return [
              ...resources,
              {
                resource: relatedPerson,
                name: relatedPerson.getFullName?.() ?? '',
                includedResources: [relatedPerson]
              },
            ];
          }, []);

          const practitionerResources = participant.reduce<ResourceWithIncludedResources[]>((resources, resource) => {
            if (resource?.resourceType !== 'Practitioner') return resources;
            const practitioner = resource;
            const wrappedPractitioner = PractitionerWrapper(practitioner);
            return [
              ...resources,
              {
                resource: wrappedPractitioner,
                includedResources: [],
              },
            ];
          }, []);

          const careTeamsFromCareTeam = participant?.filter((e) => e?.resourceType === 'CareTeam') ?? [];

          return [
            ...resources,
            {
              resource: careTeam,
              includedResources: [
                ...careTeamsFromCareTeam,
                ...(!onlyCareTeam ? [...prRelatedResources, ...relatedPersonResources, ...practitionerResources] : []),
              ],
            },
          ];
        },
        []
      );
      updateCareTeamState({ careTeams: mappedCareTeams });
    } catch (error) { return []} 
    finally { updateCareTeamState({ isCareTeamsLoading: false }); }
  }, [resourceType, patient, userOrgFhirId]);

  useEffect(() => {
    getCareTeamWithMembers();
  }, [getCareTeamWithMembers]);

  const [practitioners, { isFetching: isPractitionerFetching, refresh: refreshPractitioners }] = usePractitioners({
    filter: practitionerFilter,
    map: PractitionerWrapper,
    autofetch: resourceType === 'Practitioner' || externalRefresh,
    ...pagination,
  });


  const [organizations, { isFetching: isOrganizationsFetching, refresh: refreshOrganizations }] = useOrganizations({
    filter: {
      '_content': filter || undefined,
      'active:not': false,
      ...( filterOrganizationCode && { type: filterOrganizationCode })
    },
    map: OrganizationWrapper,
    autofetch: resourceType === 'Organization',
    ...pagination,
  });

  const [relatedPersons, { isFetching: isRelatedPerson, refresh: refreshRelatedPersons }] = useRelatedPersons({
    filter: {
      name: filter || undefined,
      patient: patient?.id ?? undefined,
      'active:not': false,
    },
    map: RelatedPersonWrapper,
    autofetch: resourceType === 'RelatedPerson',
    ...pagination,
  });

  // Add effect to refresh all resource types when externalRefresh changes
  useEffect(() => {
    if (externalRefresh) {
      // Invalidate practitioner-related queries
      queryClient.invalidateQueries({ 
        predicate: (query) => query.queryKey[0] === 'Practitioner' || 
                             query.queryKey[0] === 'PractitionerRole'
      });
      
      if (resourceType === 'Practitioner' && refreshPractitioners) {
        refreshPractitioners();
      }
      if (resourceType === 'PractitionerRole' && getPractitionerRoles) {
        getPractitionerRoles();
      }
      if (resourceType === 'Organization' && refreshOrganizations) {
        refreshOrganizations();
      }
      if (resourceType === 'RelatedPerson' && refreshRelatedPersons) {
        refreshRelatedPersons();
      }
    }
  }, [externalRefresh, resourceType, refreshPractitioners, refreshOrganizations, refreshRelatedPersons, getPractitionerRoles, queryClient]);

  const careTeamsFilter = useMemo(() => {
    if (resourceType !== 'CareTeam') return [];
    if (!filter) return careTeams || [];
    const searchByString = filter?.toLowerCase().trim() ?? '';
    let filteredCareTeams: any = [];
    if (searchByString.length >= 3) {
      filteredCareTeams = careTeams?.filter(
        (careTeam) =>
          searchIfContainedInObj(careTeam?.resource, ['name'], searchByString)
      );
    }
    return filteredCareTeams;
  }, [resourceType, careTeams, filter]);

  const memoizedFilteredCareTeams = useMemo(() => {
    if (resourceType !== 'CareTeam') return [];
    return careTeamsFilter.map((careTeam) => ({
      ...careTeam,
      includedResources: getIncludedResources(careTeam, includeMembersInactive),
    }));
  }, [careTeamsFilter, includeMembersInactive, getIncludedResources]);

  const isFetching =
    isCareTeamsLoading ||
    isPractitionerFetching ||
    isPractitionerRolesLoading ||
    isOrganizationsFetching ||
    isRelatedPerson;

  const getResourceByResourceType = useCallback(
    (resourceType: string) => {
      switch (resourceType) {
        case 'CareTeam':
          return memoizedFilteredCareTeams;
        case 'Practitioner':
          return practitioners;
        case 'PractitionerRole':
          return practitionerRoles;
        case 'Organization':
          return organizations;
        case 'RelatedPerson':
          return relatedPersons;
        case 'Patient':
          return [patient];
        case 'Me': 
          return practitionerRoles;
      }
    },
    [
      patient, 
      memoizedFilteredCareTeams, 
      practitioners, 
      practitionerRoles, 
      organizations, 
      relatedPersons
    ]
  );

  const handleFilterChange = useCallback(
    debounce((value) => {
      updateState({ filter: value });
    }, 600),
    []
  );

  const showMemberTypeSelectionField = showMemberTypeField || isEditable;
  const memberTypes = memberTypesByConfig 
    ? memberTypesByConfig?.filter(({ value }) => value !== 'Patient')
    : !isEditable
    ? getMemberResourceTypes(!!patient)
    : getMemberResourceTypes(!!patient).filter(({ value }) => value !== 'Patient');

  const [openRows, setOpenRows] = useState<GridRowId[]>([]);

  const toggleRow = (id: string) => {
    setOpenRows((openRows) => {
      if (openRows.includes(id)) {
        return openRows.filter((openRow) => openRow !== id);
      }
      return [...openRows, id];
    });
  };

  const handleMemberSelection = useCallback(
    (member: CareTeamMember, isMemberSelected: boolean): void => {
      if (isMemberSelected) {
        setMembersSelected((membersSelected) =>
          membersSelected.filter(
            ({ id, memberType }) =>
              getReference(memberType, id) !== getReference(member.memberType, member.id)
          )
        );
      } else {
        setMembersSelected((membersSelected) => [...membersSelected, member]);
      }
    },
    []
  );

  const handleMemberEditableDataChange = useCallback(
    (
      memberEditableData: MemberEditableData | undefined | null,
      newData: Partial<MemberEditableData>
    ) => {
      if (!memberEditableData) {
        const member: MemberEditableData = {
          id: '',
          memberType: '',
          startDate: null,
          endDate: null,
          role: null,
          error: {
            startDate: null,
          },
          ...newData,
        };
        if (checkIfDateIsAfterAnother(member?.startDate ?? null, member?.endDate ?? null)) {
          member.error.startDate = "Start Date can't be after End Date";
        } else {
          member.error.startDate = null;
        }
        setMembersEditableData([...membersEditableData, member]);
        return;
      }
      for (const [key, value] of Object.entries(newData) as Array<
        [keyof MemberEditableData, any]
      >) {
        if (!memberEditableData?.hasOwnProperty?.(key)) continue;
        memberEditableData[key] = value;
      }
      if (
        checkIfDateIsAfterAnother(
          memberEditableData?.startDate ?? null,
          memberEditableData?.endDate ?? null
        )
      ) {
        memberEditableData.error.startDate = "Start Date can't be after End Date";
      } else {
        memberEditableData.error.startDate = null;
      }
      setMembersEditableData([...membersEditableData]);
    },
    [membersEditableData]
  );

  const defaultEditableColumns: PositionedGridColDef[] = useMemo(
    () => [
      {
        field: 'role',
        headerName: 'Role',
        sortable,
        position: 4,
        ...getDefaultColumnWidth(resourceType),
        renderCell: (params) => {
          const { id, memberType } = params.row as GridRow;
          const memberEditableData = findMemberEditableData(id, memberType);
          if (memberType !== 'PractitionerRole')
            return <Typography> {NOTAPPLICABLE} </Typography>;

          return (
            <Autocomplete
              value={memberEditableData?.role ?? null}
              fullWidth
              onChange={(_: React.SyntheticEvent, role) => {
                handleMemberEditableDataChange(memberEditableData, {
                  id,
                  memberType,
                  role,
                });
              }}
              options={roles ?? []}
              getOptionLabel={({ display }: Coding) => display ?? ''}
              renderInput={(params) => (
                <TextField {...params} label={'Role'} variant="outlined" />
              )}
            />
          );
        },
      },
      {
        field: 'startDate',
        headerName: 'Start Date',
        sortable,
        position: 5,
        ...getDefaultColumnWidth(resourceType, columnsDefaultWidth + 30),
        ...(resourceType ? { flex: 1.6 } : {}),
        renderCell: (params) => {
          const { id, memberType } = params.row as GridRow;
          const memberEditableData = findMemberEditableData(id, memberType);
          return (
            <>
              {isAllowedToEdit ? (
                <DatePickerMoment
                  disabled={!!isLoading}
                  value={memberEditableData?.startDate ?? null}
                  error={memberEditableData?.error?.startDate ?? null}
                  label={'Start Date'}
                  onChange={(value) => {
                    handleMemberEditableDataChange(memberEditableData, {
                      id,
                      memberType,
                      startDate: value,
                    });
                  }}
                />
              ) : (
                <Typography>{memberEditableData?.startDate ?? null}</Typography>
              )}
            </>
          );
        },
      },
      {
        field: 'endDate',
        headerName: 'End Date',
        sortable,
        position: 6,
        ...getDefaultColumnWidth(resourceType, columnsDefaultWidth + 30),
        ...(resourceType ? { flex: 1.6 } : {}),
        renderCell: (params) => {
          const { id, memberType } = params.row as GridRow;
          const memberEditableData = findMemberEditableData(id, memberType);
          return (
            <>
              {isAllowedToEdit ? (
                <DatePickerMoment
                  disabled={!!isLoading}
                  value={memberEditableData?.endDate ?? null}
                  label={'End Date'}
                  onChange={(value) => {
                    handleMemberEditableDataChange(memberEditableData, {
                      id,
                      memberType,
                      endDate: value,
                    });
                  }}
                />
              ) : (
                <Typography>{memberEditableData?.endDate ?? null}</Typography>
              )}
            </>
          );
        },
      },
    ],
    [resourceType, findMemberEditableData, handleMemberEditableDataChange]
  );

  const translate = useCallback((item: string) => i18n(`practitioner.${item}`, 'crs'), [i18n]);

  interface PositionedGridColDef extends GridColDef {
    position?: number;
  }

  const defaultColumns: Record<string, PositionedGridColDef> = useMemo(
    () => ({
      ...(isEditable ? {} : {expand: {
        field: 'expand',
        headerName: '',
        sortable,
        ...getDefaultColumnWidth(resourceType),
        renderCell: (params) => {
          return (
            <IconButton
              sx={{ padding: 0 }}
              onClick={() => {
                toggleRow(params?.row?.id);
              }}
            >
              {openRows.includes(params?.row?.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )
        }
      }}),
      name: {
        field: 'name',
        headerName: 'Name',
        sortable,
        ...getDefaultColumnWidth(resourceType),
      },
      memberType: {
        field: 'memberType',
        headerName: 'Member Type',
        sortable,
        ...getDefaultColumnWidth(resourceType),
      },
      identifier: {
        field: 'identifier',
        headerName: 'Identifier',
        sortable,
        ...getDefaultColumnWidth(resourceType),
      },
      associatedOrg: {
        field: 'associatedOrg',
        headerName: resourceType ? 'Organization & Role' : 'Organization',
        sortable,
        ...getDefaultColumnWidth(resourceType),
        renderCell: (params) => {
          const { associatedOrg, roleName } = params.row as GridRow;
          return (
            <Box>
              <CellRow
                tooltipTitle={translate('Organization')}
                shouldTruncateText={false}
                title={capitalize(associatedOrg)}
                Icon={<Business sx={{ ...iconSize }} htmlColor={iconGreyFillColor}/>}
              />
              {resourceType && 
                <CellRow
                  tooltipTitle={translate('role')}
                  shouldTruncateText={false}
                  title={capitalize(roleName ?? '')}
                  Icon={<Work sx={{ ...iconSize }} htmlColor={iconGreyFillColor}/>}
                />
              }
            </Box>
          );
        },
      },
      // address: {
      //   field: 'address',
      //   headerName: translate('address'),
      //   sortable,
      //   position: 7,
      //   ...getDefaultColumnWidth(resourceType),
      //   renderCell: (params) => {
      //     const row = params.row as GridRow;
      //     const practitioner = row?.includedResources?.
      //       find((e: FhirResource) => e?.resourceType === 'Practitioner') as WrappedPractitioner;
      //     const organization = row?.resource?.resourceType === 'Organization' ? row?.resource as WrappedOrganization : null;
      //     const address = practitioner 
      //       ? practitioner?.getPrimaryAddressFormatted() 
      //       : organization 
      //       ? formatUSAddress(organization?.getPrimaryAddress()) 
      //       : '';

      //     return (
      //       <CellRow
      //         tooltipTitle={translate('address')}
      //         shouldTruncateText={false}
      //         title={address}
      //       />
      //     );
      //   },
      // },
      ...(isEditable ? {} : {npi: {
        sortable: false,
        field: 'npi',
        position: 7,
        headerName: translate('npi'),
        ...getDefaultColumnWidth(resourceType),
        renderCell: (params) => {
          const row = params.row as GridRow;
          const practitioner = row?.includedResources?.
            find((e: FhirResource) => e?.resourceType === 'Practitioner') as WrappedPractitioner;
          return <CellRow shouldTruncateText={false} title={practitioner?.getNPI()} />;
        },
      }}),
      details: {
        sortable: false,
        field: 'details',
        position: 8,
        headerName: translate('details'),
        ...getDefaultColumnWidth(resourceType),
        renderCell: (params) => {
          const row = params.row as GridRow;
          const practitioner = row?.includedResources?.
            find((e: FhirResource) => e?.resourceType === 'Practitioner') as WrappedPractitioner;
  
          return (
            <Box>
              <CellRow
                tooltipTitle={translate('practitionerName')}
                shouldTruncateText={false}
                title={capitalize(practitioner?.getFullName() ?? '')}
                Icon={<Iconify icon="mdi:stethoscope" sx={{ ...iconSize }} color={iconGreyFillColor} />}
              />
  
              <CellRow
                tooltipTitle={translate('gender')}
                shouldTruncateText={false}
                title={capitalize(practitioner?.gender ?? '')}
                Icon={<Wc sx={{ ...iconSize }} htmlColor={iconGreyFillColor} />}
              />
              <CellRow
                tooltipTitle={translate('dob')}
                shouldTruncateText={false}
                title={ practitioner?.birthDate
                  ? practitioner?.getBirthDate()
                  : null}
                Icon={
                  <Cake
                    sx={{ ...iconSize, top: -2, position: 'relative' }}
                    htmlColor={iconGreyFillColor}
                  />
                }
              />
            </Box>
          );
        },
      },
      contactDetails: {
        sortable: false,
        field: 'contactDetails',
        position: 9,
        headerName: translate('contactDetails'),
        ...getDefaultColumnWidth(resourceType),
        renderCell: (params) => {
          const row = params.row as GridRow;

          const practitioner = row?.includedResources?.find(
            (e: FhirResource): e is WrappedPractitioner => e?.resourceType === 'Practitioner'
          );

          const relatedPerson = row?.resource?.resourceType === 'RelatedPerson' ? row?.resource : null;

          const contactSource = practitioner ?? relatedPerson;

          const phone = contactSource?.getPrimaryPhone?.()?.value ?? null;
          const email = contactSource?.getPrimaryEmail?.()?.value ?? null;

          return (
            <Box>
              <CellRow
                tooltipTitle={translate('phone')}
                shouldTruncateText={false}
                title={phone}
                Icon={<Phone sx={{ ...iconSize }} htmlColor={iconGreyFillColor} />}
              />
              <CellRow
                tooltipTitle={translate('email')}
                shouldTruncateText={false}
                title={email}
                Icon={<Mail sx={{ ...iconSize }} htmlColor={iconGreyFillColor} />}
              />
            </Box>
          );
        },
      },
      relationship: {
        sortable: false,
        field: 'relationship',
        position: 10,
        headerName: translate('relationship'),
        ...getDefaultColumnWidth(resourceType),
        renderCell: (params) => {
          const row = params.row as GridRow;

          const relatedPerson = row?.resource?.resourceType === 'RelatedPerson' ? row?.resource : null;

          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}> 
              {relatedPerson?.relationship?.map(({ coding, text }) => (
                <Chip
                  clickable={false}
                  size="small"
                  key={coding?.[0]?.code}
                  label={text ?? coding?.[0]?.display}
                  sx={{ m: 0.2, textTransform: 'capitalize' }}
                />
              ))}
            </Box>
          );
        },
      }
    }),
    [resourceType, findMemberEditableData, handleMemberEditableDataChange, roles]
  );

  const columnsMapping: ResourceMapping<GridColDef[]> = useMemo(
    () => ({
      Practitioner: [
        {
          ...defaultColumns.name,
          flex: 1,
        },
        {
          ...defaultColumns.identifier,
          flex: 1,
        },
      ],
      Patient: [
        {
          ...defaultColumns.name,
          flex: 1,
        },
        {
          ...defaultColumns.identifier,
          flex: 1,
        },
      ],
      CareTeam: [
        ...(isEditable ? [] : [{ ...defaultColumns.expand, flex: 0.2 }]),
        {
          ...defaultColumns.name,
          flex: 1,
        },
        {
          ...defaultColumns.associatedOrg,
          flex: 1,
        },
      ],
      PractitionerRole: [
        ...(isEditable ? [] : [{ ...defaultColumns.npi, flex: 1 }]),
        {
          ...defaultColumns.details,
          flex: 1,
        },
        {
          ...defaultColumns.contactDetails,
          flex: 1,
        },
        {
          ...defaultColumns.associatedOrg,
          flex: 1,
        },
      ],
      Organization: [
        {
          ...defaultColumns.name,
          flex: 1,
        },
        {
          ...defaultColumns.identifier,
          flex: 1,
        },
        // {
        //   ...defaultColumns.address,
        //   flex: 2,
        // },
      ],
      RelatedPerson: [
        {
          ...defaultColumns.name,
          flex: 1,
        },
        {
          ...defaultColumns.identifier,
          flex: 1,
        },
        {
          ...defaultColumns.relationship,
          flex: 1,
        },
        {
          ...defaultColumns.contactDetails,
          flex: 1
        }
      ],
    }),
    [defaultColumns, isEditable]
  );

  const actionButtonColumn: GridColDef = useMemo(
    () => ({
      field: 'edit',
      headerName: '',
      align: 'center',
      sortable: false,
      renderCell: (params) => {
        const row = params.row as GridRow;
        const isMemberSelected = !!membersSelected.find(
          (memberSelected) =>
            memberSelected.id === row?.id && memberSelected.memberType === row?.memberType
        );

        const shouldDisableButton =
          isEditable &&
          ((!isAllowedToAdd && !isMemberSelected) || (!isAllowedToDelete && isMemberSelected));
        return (
          <Button
            disabled={!!isLoading || !!shouldDisableButton}
            color={(!isEditable && !onlyCareTeam) ? 'primary' : isMemberSelected ? 'error' : 'primary'}
            sx={{
              height: 36,
              width: 63,
            }}
            onClick={() => {
              if (isEditable) {
                handleMemberSelection(row as CareTeamMember, isMemberSelected); 
                return;
              }
              const resourceToSelect = row?.resource as MemberWrappedResourceTypes;
              if (!resourceToSelect) {
                console.warn('SearchMember: Attempted to select resource but row.resource is missing', { row });
                return;
              }
              onSelectResource?.(
                resourceToSelect,
                row?.includedResources ?? [],
                {...row, isSelected: isMemberSelected}
              );
              if (onlyCareTeam) {
                handleMemberSelection(row as CareTeamMember, isMemberSelected);
                return;
              }
            }}
            variant="contained"
          >
            {(!isEditable && !onlyCareTeam) ? 'Select' : isMemberSelected ? 'Remove' : 'Add'}
          </Button>
        );
      },
    }),
    [membersSelected, isEditable, onlyCareTeam, handleMemberSelection, onSelectResource,filterRole]
  );

  const getRows = useCallback(() => {
    if (isEditable) {
      if (!resourceType && !filter) {
        return membersSelected;
      }

      if (!resourceType && filter) {
        return membersSelected?.filter((memberSelected) =>
          searchIfContainedInObj(
            memberSelected,
            [
              'id',
              'name',
              'memberType',
              'identifier',
              'associatedOrg',
              'role', 
              'startDate',
              'endDate',
            ],
            filter ?? ''
          )
        );
      }
    }

    const resources = getResourceByResourceType(resourceType as string);
    
    return resources?.map?.((resource) =>
      getRowsMapping?.[resourceType === 'Me' ? 'PractitionerRole' : resourceType as MemberResourceTypes]?.(
        resource as MemberWrappedResourceTypes, 
        configurations?.mpi
      )
    );
  }, [resourceType, membersSelected, getResourceByResourceType, isEditable, filter, configurations]);

  const getColumns = useCallback(() => {
    const columnsByResourceType = (columnsMapping?.[resourceType === 'Me' ? 
      'PractitionerRole' : resourceType as MemberResourceTypes] ??
    []) as GridColDef[];
    if (!isEditable) return [...columnsByResourceType, actionButtonColumn];

    if (resourceType)
      return [...columnsByResourceType,
        ...(resourceType === 'CareTeam' && 
          selectCareTeam
            ? [actionButtonColumn] : resourceType !== 'CareTeam'
            ? [actionButtonColumn]: []
        )
      ];

    const commonMemberType =
      membersSelected?.length > 0 &&
      membersSelected.every((m) => m.memberType === membersSelected[0].memberType)
        ? (membersSelected[0].memberType as MemberResourceTypes)
        : null;

    const buildOrderedColumns = (excludeDetails: boolean, excludeOrg: boolean) => {
      const filtered = Object.values(defaultColumns).filter(
        (col) =>
          (col?.field !== 'details' || !excludeDetails) &&
          (col?.field !== 'associatedOrg' || !excludeOrg)
      );
      return [
        ...filtered,
        ...defaultEditableColumns,
      ]
        ?.map((e, index) => {
          if (e && !e?.position) {
            e.position = index;
          }
          return e;
        })
        ?.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    };

    if (commonMemberType === 'PractitionerRole' || commonMemberType === 'Me') {
      const orderedColumns = buildOrderedColumns(false, false);
      return [...orderedColumns, actionButtonColumn];
    }
    if (commonMemberType === 'RelatedPerson') {
      const orderedColumns = buildOrderedColumns(true, true);
      return [...orderedColumns, actionButtonColumn];
    }
    const orderedColumns = buildOrderedColumns(true, true);
    return [
      ...orderedColumns,
      ...(resourceType === 'CareTeam' && selectCareTeam
        ? [actionButtonColumn]
        : resourceType !== 'CareTeam'
          ? [actionButtonColumn]
          : []),
    ];
  }, [
    defaultColumns,
    actionButtonColumn,
    resourceType,
    columnsMapping,
    defaultEditableColumns,
    isEditable,
    filterRole,
    membersSelected,
  ]);

  const rows = useMemo(() => getRows(), [getRows]);

  const columns = useMemo(() => getColumns(), [getColumns]);

  function handleChange(id: string): void {
    const updatedState = includeMembersInactive.includes(id)
      ? includeMembersInactive.filter((memberId) => memberId !== id)
      : [...includeMembersInactive, id];
  
    setIncludeMembersInactive(updatedState);
  };

  const SearchField = () => (
    <SearchTextField
      disabled={!!isLoading}
      value={searchTextField}
      onChange={({ target: { value } }) => {
        updateState({ searchTextField: value });
        handleFilterChange(value);
      }}
      placeholder={`Search ${
        titleMapping?.[resourceType as MemberResourceTypes]
          ? `by ${i18n(titleMapping[resourceType as MemberResourceTypes], 'crs')}`
          : ''
      }...`}
      fullWidth
    />
  );
  
  const SelectResourceType = (label: string) => (
    <FormControl fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        disabled={!!isLoading}
        value={resourceType}
        label={label}
        onChange={(event) => {
          const selectedValue = event.target.value;
          const name = memberTypes?.find((e) => e?.value === selectedValue)?.label;
          updateState({ resourceType: selectedValue, resourceName: name });
  
          handlerMemberPractitionerRole?.({
            isPractitionerRole: selectedValue === 'PractitionerRole',
            disabledSave: true,
          });
        }}
      >
        {memberTypes.map(({ label, value }) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
  
  const ClearOrApplyButton = () => (
    <Button
      disabled={!!isLoading}
      sx={{
        ml: 2,
        py: 2,
        px: 3,
        alignSelf: 'center',
        width: 78,
        height: 26,
        border: 0,
        '&:hover': { border: 0 },
      }}
      onClick={() => {
        updateState({ searchTextField: '', resourceType: null, filter: null });
        handlerMemberPractitionerRole?.({ isPractitionerRole: false, disabledSave: false });
        onClear?.();
      }}
      variant={!resourceType ? 'outlined' : 'contained'}
      color={!resourceType ? 'error' : 'primary'}
      startIcon={!resourceType && <DeleteOutlineIcon />}
    >
      <Typography fontSize="0.9rem" fontWeight="bold" sx={{ mr: '2px' }}>
        {!resourceType ? 'Clear' : 'Apply'}
      </Typography>
    </Button>
  );

  return (
    <>
      {isEditable ? (
        <>
          {!resourceType && (
            <GridSection mt={0}>
              <GridItem xs={12} display="flex" alignItems="center">
                <Typography variant="h6">
                  {i18n('case.details.careteams.addNewMember', 'crs')}
                </Typography>
              </GridItem>
            </GridSection>
          )}

          <GridSection mt={2}>
            {showMemberTypeSelectionField && 
              <GridItem xs={3}>{SelectResourceType('Member Type')}</GridItem>
            }

            <GridItem
              xs={8.7}
              display="flex"
              justifyContent={!resourceType ? 'row' : 'flex-end'}
            >
              {!resourceType && SearchField()}
              {ClearOrApplyButton()}
            </GridItem>
          </GridSection>

          {resourceType && (
            <>
              <GridSection mt={2}>
                <GridItem xs={12} display="flex" alignItems="center" mb={2}>
                  <Typography variant="h6">{`${resourceName} List`}</Typography>
                </GridItem>
              </GridSection>

              <GridSection mt={0}>
                <GridItem
                  xs={resourceType !== 'PractitionerRole' ? 12 : 8}
                  display="flex"
                  justifyContent="row"
                >
                  {SearchField()}
                </GridItem>

                {resourceType === 'PractitionerRole' && (
                  <GridItem xs={4} sx={{ display: 'flex', ml: 2, justifyContent: 'center' }}>
                    {internalExternalOrg.map(({ code, display }) => (
                      <FormControlLabel
                        key={code}
                        control={
                          <Checkbox
                            checked={internalExternal?.includes(code || '')}
                            onChange={({ target: { checked } }) => {
                              const newCode = code || '';
                              setInternalExternal((prev) => {
                                if (checked) {
                                  return newCode === 'all' ? ['all'] : [...prev.filter(c => c !== 'all'), newCode];
                                }
                                return prev.filter((c) => c !== newCode);
                              });
                            }}
                          />
                        }
                        label={i18n(`patients.details.serviceRequests.searchService.${code}`, 'crs')}
                      />
                    ))}
                  </GridItem>
                )}
              </GridSection>
            </>
          )}
        </>
      ) : (
        <GridSection mt={0}>
          {showMemberTypeSelectionField && (
            <GridItem xs={3}>{SelectResourceType('Owner Type')}</GridItem>
          )}

          <GridItem
            xs={!showMemberTypeSelectionField ? 12 : 8.7}
            display="flex"
            alignItems="center"
            gap={2}
          >
            <Box sx={{ flex: 1 }}>
              {SearchField()}
            </Box>
            
            {resourceType === 'PractitionerRole' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {internalExternalOrg.map(({ code, display }) => (
                  <FormControlLabel
                    key={code}
                    control={
                      <Checkbox
                        checked={internalExternal?.includes(code || '')}
                        onChange={({ target: { checked } }) => {
                          const newCode = code || '';
                          setInternalExternal((prev) => {
                            if (checked) {
                              return newCode === 'all' ? ['all'] : [...prev.filter(c => c !== 'all'), newCode];
                            }
                            return prev.filter((c) => c !== newCode);
                          });
                        }}
                      />
                    }
                    label={i18n(`patients.details.serviceRequests.searchService.${code}`, 'crs')}
                  />
                ))}
              </Box>
            )}
            
            {
              (['PractitionerRole'].includes(resourceType ?? '') 
                && onAddNewPractitioner) && 
                (
                  <Button
                    disabled={!!isLoading}
                    sx={{
                      py: 2,
                      px: 3,
                      width: 'auto',
                      minWidth: 180,
                      height: 36,
                      border: 0,
                      backgroundColor: '#00ab55',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        border: 0,
                        backgroundColor: '#007b55',
                      },
                    }}
                    onClick={onAddNewPractitioner}
                    variant="contained"
                    color="primary"
                    startIcon={<Iconify icon="eva:plus-fill" />}
                  >
                    <Typography fontSize="0.9rem" fontWeight="bold">
                      {translate('newPractitionerTitle')}
                    </Typography>
                  </Button>
                )
            }

            <Button
              disabled={!!isLoading}
              sx={{
                py: 2,
                px: 3,
                color: '#ff5630',
                width: 'auto',
                minWidth: 78,
                height: 36,
                border: 0,
                whiteSpace: 'nowrap',
                '&:hover': {
                  border: 0,
                  backgroundColor: 'rgba(255, 86, 48, 0.16)',
                },
              }}
              onClick={() => {
                updateState({ searchTextField: '', resourceType: null, filter: null });
                onClear?.();
              }}
              variant="outlined"
              color="inherit"
            >
              <DeleteOutlineIcon htmlColor="#ff5630" sx={{ mr: '1px' }} />
              <Typography color="#ff5630" fontSize="0.9rem" fontWeight="bold">
                Clear
              </Typography>
            </Button>
          </GridItem>
        </GridSection>
      )}
          
      {headerComponents ? (
        <Grid container xs={12} mt={1} mb={1}>
          <Grid item xs={12} sx={{ display: 'flex' }}>
            {headerComponents}
          </Grid>
        </Grid>
      ) : null}

      <GridSection mt={headerComponents ? 0 : 3}>
        <GridItem xs={12}>
          <Box sx={{ width: '100%', height: 450 }}>
            {(!!resourceType || isEditable) && (
              <DataGrid
                rows={rows ?? []}
                sx={{
                  width: '100% !important',
                  '& .MuiDataGrid-columnHeader': { backgroundColor: '#f4f6f8' },
                  '& .MuiDataGrid-cell': {
                    border: 0,
                  },
                  '& .MuiDataGrid-row': {
                    borderBottom: '1px solid #EAECF0',
                    '&:nth-of-type(odd)': {
                      backgroundColor: '#FAFAFA',
                    },
                    '&:hover': {
                      backgroundColor: '#F5F6F7',
                    },
                  },
                }}
                loading={isFetching}
                getRowSpacing={() => ({ bottom: 5 })}
                getRowHeight={() => isEditable ? 145 : 'auto'}
                getEstimatedRowHeight={() => 90}
                columns={columns}
                components={{
                  Row: (rowProps: GridRowProps) => {
                    return isEditable ? (
                      <GridRow {...rowProps} />
                    ) : (
                      <CollapsibleRow
                        rowProps={rowProps}
                        openRows={openRows}
                        renderCollapsibleContent={(careTeam: any) => {
                          const countMember = careTeam?.includedResources?.length;
                          
                          const mappedIncludedResources = (() => {
                            if (!careTeam?.includedResources || !Array.isArray(careTeam.includedResources)) return [];
                            
                            return careTeam.includedResources
                              .map((includedResource: any) => {
                                try {
                                  let resourceToMap: MemberWrappedResourceTypes | ResourceWithIncludedResources | null = null;
                                  let includedResourcesForRow: FhirResource[] = [];
                                  let isResourceWithIncludedStructure = false;
                                  
                                  if (includedResource?.resource) {
                                    const resourceType = includedResource.resource?.resourceType;
                                    
                                    if (resourceType === 'PractitionerRole') {
                                      resourceToMap = includedResource as ResourceWithIncludedResources;
                                      isResourceWithIncludedStructure = true;
                                    } else {
                                      resourceToMap = includedResource.resource as MemberWrappedResourceTypes;
                                      includedResourcesForRow = includedResource.includedResources || [];
                                    }
                                  } 
                                  else if (includedResource?.resourceType) {
                                    const wrappedResource = wrapResource(includedResource);
                                    if (wrappedResource) {
                                      resourceToMap = wrappedResource as MemberWrappedResourceTypes;
                                    }
                                  }
                                  else {
                                    console.warn('SearchMember: Skipping invalid includedResource', includedResource);
                                    return null;
                                  }
                                  
                                  if (!resourceToMap) {
                                    console.warn('SearchMember: Invalid resource to map', resourceToMap);
                                    return null;
                                  }
                                  
                                  if (isResourceWithIncludedStructure) {
                                    const rwi = resourceToMap as ResourceWithIncludedResources;
                                    if (!rwi.resource || !rwi.resource.resourceType || !rwi.resource.id) {
                                      console.warn('SearchMember: Invalid ResourceWithIncludedResources', rwi);
                                      return null;
                                    }
                                  } else {
                                    const resource = resourceToMap as MemberWrappedResourceTypes;
                                    if (!resource.resourceType || !resource.id) {
                                      console.warn('SearchMember: Invalid resource to map', resource);
                                      return null;
                                    }
                                  }
                                  
                                  const resourceType = isResourceWithIncludedStructure
                                    ? (resourceToMap as ResourceWithIncludedResources).resource.resourceType
                                    : (resourceToMap as MemberWrappedResourceTypes).resourceType;
                                  
                                  const mappingFn = getRowsMapping[resourceType as MemberResourceTypes];
                                  
                                  if (!mappingFn) {
                                    console.warn('SearchMember: No mapping function for resource type', resourceType);
                                    return null;
                                  }
                                  
                                  const mappedRow = mappingFn(
                                    resourceToMap as any, 
                                    configurations?.mpi
                                  );
                                  
                                  if (!mappedRow || !mappedRow.id || !mappedRow.memberType) {
                                    console.warn('SearchMember: Invalid mapped row', mappedRow);
                                    return null;
                                  }
                                  
                                  if (!mappedRow.resource) {
                                    if (isResourceWithIncludedStructure) {
                                      mappedRow.resource = (resourceToMap as ResourceWithIncludedResources).resource as MemberWrappedResourceTypes;
                                    } else {
                                      mappedRow.resource = resourceToMap as MemberWrappedResourceTypes;
                                    }
                                  }
                                  
                                  if (isResourceWithIncludedStructure) {
                                    const rwi = resourceToMap as ResourceWithIncludedResources;
                                    if (rwi.includedResources?.length > 0 && !mappedRow.includedResources) {
                                      mappedRow.includedResources = rwi.includedResources;
                                    }
                                  } else if (includedResourcesForRow.length > 0 && !mappedRow.includedResources) {
                                    mappedRow.includedResources = includedResourcesForRow;
                                  }
                                  
                                  return mappedRow;
                                } catch (error) {
                                  console.error('SearchMember: Error mapping includedResource', error, includedResource);
                                  return null;
                                }
                              })
                              .filter((row): row is GridRow => row !== null && row !== undefined);
                          })();
                          
                          return  (
                            <Box 
                              sx={{ my: 2}}
                            >
                              {countMember ? (
                                <Box display="flex" flexDirection="row" alignItems="center">
                                  <Typography variant="body2">{`${countMember} Members`}</Typography>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={includeMembersInactive?.includes(careTeam?.resource?.id || "")}
                                        name="includeInactive"
                                        onChange={() => handleChange(careTeam?.resource?.id || "")}
                                      />
                                    }
                                    label="Include Inactive"
                                    style={{ marginLeft: 'auto' }}
                                  />
                                </Box>
                              ): null}
  
                              <DataGrid
                                columns={[
                                  {
                                    field: 'display',
                                    headerName: 'Name',
                                    flex: 1,
                                    sortable: false,
                                    renderCell: (params) => {
                                      if (!params?.row) return "";
                                      const rowResource = params.row as GridRow;
                                      
                                      if (rowResource?.name) {
                                        return rowResource.name;
                                      }
                                      
                                      const resource = rowResource?.resource;
                                      if (resource && ('getFullName' in resource) && typeof resource.getFullName === 'function') {
                                        try {
                                          return resource.getFullName() || "";
                                        } catch (error) {
                                          console.warn('SearchMember: Error calling getFullName', error);
                                        }
                                      }
                                      
                                      const includedResources = rowResource?.includedResources;
                                      if (includedResources?.[0]) {
                                        try {
                                          const wrappedIncludedR = wrapResource(includedResources[0]);
                                          if (wrappedIncludedR?.getFullName) {
                                            return wrappedIncludedR.getFullName() || "";
                                          }
                                        } catch (error) {
                                          console.warn('SearchMember: Error wrapping includedResource', error);
                                        }
                                      }
                                      
                                      return "";
                                    }
                                  },
                                  {
                                    field: 'contact',
                                    headerName: 'Contact Details',
                                    flex: 1,
                                    sortable: false,
                                    renderCell: (params) => {
                                      if (!params?.row) {
                                        return <Box />;
                                      }
                                      
                                      const rowResource = params.row as GridRow;
                                      const resource = rowResource?.resource;
                                      let getPhone: string | null = null;
                                      let getEmail: string | null = null;
                                      
                                      if (resource) {
                                        try {
                                          if (('getPrimaryPhone' in resource) && typeof resource.getPrimaryPhone === 'function') {
                                            getPhone = resource.getPrimaryPhone()?.value ?? null;
                                          }
                                          if (('getPrimaryEmail' in resource) && typeof resource.getPrimaryEmail === 'function') {
                                            getEmail = resource.getPrimaryEmail()?.value ?? null;
                                          }
                                        } catch (error) {
                                          console.warn('SearchMember: Error getting contact from resource', error);
                                        }
                                      }
                                      
                                      if ((!getPhone || !getEmail) && rowResource?.includedResources?.[0]) {
                                        try {
                                          const wrappedIncludedR = wrapResource(rowResource.includedResources[0]);
                                          if (wrappedIncludedR) {
                                            if (!getPhone && wrappedIncludedR.getPrimaryPhone) {
                                              getPhone = wrappedIncludedR.getPrimaryPhone()?.value ?? null;
                                            }
                                            if (!getEmail && wrappedIncludedR.getPrimaryEmail) {
                                              getEmail = wrappedIncludedR.getPrimaryEmail()?.value ?? null;
                                            }
                                          }
                                        } catch (error) {
                                          console.warn('SearchMember: Error getting contact from includedResources', error);
                                        }
                                      }
  
                                      return (
                                        <Box>
                                          <CellRow
                                            tooltipTitle={translate('phone')}
                                            shouldTruncateText={false}
                                            title={getPhone}
                                            Icon={<Phone sx={{ height: 15, width: 15 }} htmlColor={"#919eab"} />}
                                          />
                                          <CellRow
                                            tooltipTitle={translate('email')}
                                            shouldTruncateText={false}
                                            title={getEmail}
                                            Icon={<Mail sx={{ height: 15, width: 15 }} htmlColor={"#919eab"} />}
                                          />
                                        </Box>
                                      )
                                    }
                                  },
                                  {
                                    field: 'role',
                                    headerName: 'Role',
                                    flex: 1,
                                    sortable: false,
                                    renderCell: (params) => {
                                      if (!params?.row) return "";
                                      
                                      const rowResource = params.row as GridRow;
                                      const resource = rowResource?.resource;
                                      
                                      if (!resource?.id || !careTeam?.resource?.participant) {
                                        return "";
                                      }
                                      
                                      try {
                                        const roleFromCareTeam = careTeam.resource.participant.find((e) => 
                                          getReferenceId(e?.member?.reference) === resource.id)?.role?.[0];
                                        
                                      return roleFromCareTeam?.text ?? roleFromCareTeam?.coding?.[0]?.display ?? "";
                                      } catch (error) {
                                        console.warn('SearchMember: Error getting role from careTeam', error);
                                        return "";
                                      }
                                    }
                                  },
                                  ...selectMember ? [actionButtonColumn] : [],
                                ]}
                                sx={{
                                  '& .MuiDataGrid-row': {
                                    borderBottom: '1px solid #EAECF0',
                                    '&:nth-of-type(odd)': {
                                      backgroundColor: '#FAFAFA',
                                    },
                                    '&:hover': {
                                      backgroundColor: '#F5F6F7',
                                    },
                                  },
                                }}
                                getRowId={(row) => {
                                  const gridRow = row as GridRow;
                                  if (gridRow?.id && gridRow?.memberType) {
                                    return `${gridRow.memberType}-${gridRow.id}`;
                                  }
                                  return uuidv4();
                                }}
                                autoHeight={true}
                                getRowSpacing={() => ({ bottom: 5 })}
                                getRowHeight={() => 'auto'}
                                getEstimatedRowHeight={() => 90}
                                rows={mappedIncludedResources}
                                components={{ Footer: () => null}}
                                isRowSelectable={() => false}
                                disableColumnMenu
                              />
                            </Box>
                          )
                        }}
                      />
                    )
                  },
                  Footer: () => <></>
                }}
                rowSpacingType="margin"
                isRowSelectable={() => false}
                disableColumnFilter
                disableColumnMenu
                density={'compact'}
              />
            )}
          </Box>
        </GridItem>
      </GridSection>
    </>
  );
};

type ResourceMapping<T> = {
  [k in MemberResourceTypes]?: T;
};

const titleMapping: ResourceMapping<string> = {
  PractitionerRole: 'patients.details.tasks.searchMember.titleMapping',
};

const sortable = false;

export const getRowsMapping: ResourceMapping<(resource: MemberWrappedResourceTypes, configurations?: any) => GridRow> = {
  Practitioner: (resource) => {
    const { getFullName, id, getIdentifier, resourceType, meta } = resource as WrappedPractitioner;
    const { identifier, type: identifierType } = getIdentifier();
    return {
      id: id ?? '',
      identifier: identifier ?? '',
      identifierType,
      name: getFullName() ?? '',
      associatedOrg: NOTAPPLICABLE,
      memberType: resourceType,
      versionId: meta?.versionId ?? '',
      resource,
    };
  },
  Patient: (resource, configurations) => {
    const { getFullName, id, getIdentifier, resourceType, meta } = resource as WrappedPatient;
    return {
      id: id ?? '',
      identifier: getIdentifier(IdentifierCode.MEDICAL_RECORD_NUMBER, configurations?.mpi?.systemMrn)?.value ?? '',
      name: getFullName() ?? '',
      memberType: resourceType,
      versionId: meta?.versionId ?? '',
      resource,
    };
  },
  CareTeam: (resource) => {
    const { resource: careTeam, includedResources } = (resource ??
      {}) as unknown as ResourceWithIncludedResources;

    const { name, id, resourceType, managingOrganizationId, managingOrganizationName, meta } = 
      (careTeam ?? {}) as WrappedCareTeam;

    return {
      id: id ?? '',
      name,
      resource: careTeam as WrappedCareTeam,
      memberType: resourceType,
      associatedOrg: managingOrganizationName ?? managingOrganizationId,
      associatedOrgId: managingOrganizationId,
      associatedOrgName: managingOrganizationName,
      role: '',
      roleId: "care-team",
      roleName: "Care Team",
      includedResources,
      versionId: meta?.versionId ?? '',
    };
  },
  PractitionerRole: (resource) => {
    const { resource: practitionerRole, includedResources } = (resource ??
      {}) as unknown as ResourceWithIncludedResources;

    const {
      getIdentifier,
      getPractitionerName,
      getRoleName,
      getRoleId,
      resourceType,
      getOrganizationId,
      getOrganizationName,
      meta
    } = (practitionerRole ?? {}) as WrappedPractitionerRole;

    const { identifier, type: identifierType } = getIdentifier();
    return {
      id: practitionerRole?.id ?? '',
      identifier: identifier ?? '',
      identifierType,
      name: getPractitionerName((includedResources ?? []) as WrappedPractitioner[]),
      memberType: resourceType,
      role: getRoleName() ?? getRoleId(),
      roleId: getRoleId(),
      roleName: getRoleName(),
      associatedOrg: getOrganizationName() ?? getOrganizationId(),
      associatedOrgId: getOrganizationId(),
      associatedOrgName: getOrganizationName((includedResources ?? []) as WrappedOrganization[]),
      resource: practitionerRole as WrappedPractitionerRole,
      includedResources,
      versionId: meta?.versionId ?? '',
    };
  },
  Organization: (resource) => {
    const { id, resourceType, name } = resource as WrappedOrganization;
    return {
      id: id ?? '',
      identifier: id ?? '',
      name: name ?? '',
      memberType: resourceType,
      resource,
    };
  },
  RelatedPerson: (resource) => {
    const { id, resourceType, getFullName } = (resource ?? {}) as WrappedRelatedPerson;
    return {
      id: id ?? '',
      identifier: id ?? '',
      name: getFullName?.() ?? '',
      memberType: resourceType,
      resource,
    };
  },
};

export const getMemberResourceTypes = (showPatient: boolean = true): MemberResourceTypeOption[] => [
  ...(showPatient ? [{ label: 'Patient', value: 'Patient' } as MemberResourceTypeOption] : []),
  // { label: 'Practitioner', value: 'Practitioner' },
  { label: 'Practitioner Role', value: 'PractitionerRole' },
  { label: 'Care Team', value: 'CareTeam' },
];

const getPractitionerRoleRelatedResources = async (careTeams: CareTeam[]) => {
  try {
    const practitionerRoleIds = careTeams.reduce<string[]>((resourceIds, careTeam) => {
      const ids = careTeam?.participant
        ?.filter?.(
          ({ member }) =>
            getReferenceType(member?.reference) === 'PractitionerRole' && !!getReferenceId(member?.reference)
        )
        ?.map?.(({ member }) => getReferenceId(member?.reference)) as string[];
      if (!ids?.length) return resourceIds;
      return [...resourceIds, ...ids];
    }, []);
    if (!practitionerRoleIds.length) return [];

    const response = await fhirClient.search<Bundle>(
      `PractitionerRole?_id=${[
        ...new Set(practitionerRoleIds),
      ].join()}&_include=PractitionerRole:practitioner`
    );

    return (
      response?.entry?.reduce?.<FhirResource[]>((resources, { resource }) => {
        if (!resource) return resources;
        return [...resources, resource];
      }, []) ?? []
    );
  } catch (error) {
    return [];
  }
};

const getRelatedPersonRelatedResources = async (careTeams: CareTeam[]) => {
  try {
    const relatedPersonIds = careTeams.reduce<string[]>((resourceIds, careTeam) => {
      const ids = careTeam?.participant
        ?.filter?.(
          ({ member }) =>
            getReferenceType(member?.reference) === 'RelatedPerson' && !!getReferenceId(member?.reference)
        )
        ?.map?.(({ member }) => getReferenceId(member?.reference)) as string[];
      if (!ids?.length) return resourceIds;
      return [...resourceIds, ...ids];
    }, []);
    if (!relatedPersonIds.length) return [];

    const response = await fhirClient.search<Bundle>(
      `RelatedPerson?_id=${[
        ...new Set(relatedPersonIds),
      ].join()}`
    );

    return (
      response?.entry?.reduce?.<FhirResource[]>((resources, { resource }) => {
        if (!resource) return resources;
        return [...resources, resource];
      }, []) ?? []
    );
  } catch (error) {
    return [];
  }
};

export default SearchMember;